import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";
import { TrendingUp, TrendingDown, MapPin, Plus, Trash2, Landmark, Building, Home, ChevronRight, CalendarDays, Info, Archive, Check, Wallet } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { motion } from "framer-motion";
import AddAssetDialog from "../components/AddAssetDialog";
import AssetDetailModal from "../components/AssetDetailModal";
import ArchiveModal from "../components/ArchiveModal";
import OnboardingTour from "../components/OnboardingTour";
import { toast } from "../components/ui/sonner";

const locationLabels = { dubai: "Dubai, UAE", north_cyprus: "North Cyprus", germany: "Germany" };
const CHART_COLORS = ["#D4AF37", "#FFFFFF", "#71717A", "#3F3F46"];


/**
 * Custom tooltip for the Total Invested Capital stacked-area chart.
 *
 * Replaces Recharts' default tooltip with the dashboard's Quiet Luxury
 * aesthetic — dark surface, hairline border, tabular numbers, and a Georgia
 * serif total row in gold. One row per active location, in the same order
 * as the legend so the visual mapping stays predictable.
 */
function StackedAreaTooltip({ active, payload, label, activeStack, symbol }) {
  if (!active || !payload || !payload.length) return null;
  // Map payload back to the activeStack order so the tooltip rows always
  // appear in the same order regardless of Recharts' internal stacking order.
  const valueByKey = Object.fromEntries(payload.map((p) => [p.dataKey, p.value]));
  const total = activeStack.reduce((sum, s) => sum + (Number(valueByKey[s.key]) || 0), 0);

  return (
    <div
      data-testid="chart-tooltip"
      style={{
        backgroundColor: "#1a1a1a",
        border: "0.5px solid #2a2a2a",
        borderRadius: "6px",
        padding: "10px 12px",
        minWidth: "180px",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <p style={{
        margin: 0, marginBottom: "8px",
        fontSize: "10px", textTransform: "uppercase",
        letterSpacing: "0.1em", color: "#555",
      }}>{label}</p>
      {activeStack.map((s) => {
        const v = Number(valueByKey[s.key]) || 0;
        if (v <= 0) return null;
        return (
          <div key={s.key} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            gap: "16px", marginBottom: "4px",
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              fontSize: "11px", color: "#bdbdbd",
            }}>
              <span style={{
                display: "inline-block", width: "8px", height: "8px",
                borderRadius: "2px", backgroundColor: s.stroke,
              }} />
              {s.label}
            </span>
            <span style={{
              fontSize: "11px", color: "#ddd", fontVariantNumeric: "tabular-nums",
            }}>
              {symbol} {Math.round(v).toLocaleString()}
            </span>
          </div>
        );
      })}
      <div style={{
        marginTop: "8px", paddingTop: "8px",
        borderTop: "0.5px solid #2a2a2a",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "12px", color: "#D4AF37",
        }}>Total</span>
        <span style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "13px", color: "#D4AF37", fontVariantNumeric: "tabular-nums",
        }}>
          {symbol} {Math.round(total).toLocaleString()}
        </span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { mode, rates, toEur, fmt, fmtSigned, display, SYMBOLS } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [performance, setPerformance] = useState([]);
  // Locations the user owns + FX-impact figure from the same endpoint —
  // populated alongside `performance` so the stacked-area chart, legend,
  // and FX badge stay in lock-step with whichever time-range is selected.
  const [perfMeta, setPerfMeta] = useState({ locations_present: [], fx_impact_eur: 0, fx_basis_available: true });
  const [perfMonths, setPerfMonths] = useState(12);
  const [alerts, setAlerts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, perfRes, alertRes] = await Promise.all([
        api.get("/portfolio/summary"),
        api.get(`/portfolio/performance?months=${perfMonths}`),
        api.get("/fx/arbitrage"),
      ]);
      setSummary(sumRes.data);
      setPerformance(perfRes.data.performance || []);
      setPerfMeta({
        locations_present: perfRes.data.locations_present || [],
        fx_impact_eur: perfRes.data.fx_impact_eur ?? 0,
        fx_basis_available: perfRes.data.fx_basis_available !== false,
      });
      setAlerts(alertRes.data.alerts || []);
      return sumRes.data;
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [perfMonths]);

  useEffect(() => { fetchData(); }, [fetchData, perfMonths]);

  const handleDeleteAsset = async (id) => {
    try {
      await api.delete(`/assets/${id}`);
      toast.success("Property archived", { description: "Restore it from the Archive button at any time." });
      fetchData();
    } catch (err) {
      toast.error("Could not archive", { description: err.response?.data?.detail });
    }
  };

  const formatEur = (v) => new Intl.NumberFormat("en-EU", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

  // Currency-aware portfolio total — convert the EUR-denominated portfolio
  // total into whatever home currency the user has selected (EUR/AED/GBP).
  const portfolioTotal = summary?.total_value_eur || 0;
  const portfolioDisplay = (() => {
    const d = display(portfolioTotal, "EUR");
    return `${d.symbol} ${Math.round(d.value).toLocaleString()}`;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pieData = summary?.by_location
    ? Object.entries(summary.by_location).map(([k, v]) => ({ name: locationLabels[k] || k, value: v }))
    : [];

  return (
    <div data-testid="dashboard-page">
      {/* Note: Dashboard has its own dedicated OnboardingTour for new users
          (Add Asset walkthrough). We intentionally do NOT render PageIntro
          here to avoid overlap. PageIntro fires on every other page. */}
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 md:mb-8">
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">Portfolio Overview</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight" data-testid="portfolio-total">
            {portfolioDisplay}
          </h1>
          <p className="text-white/60 text-xs sm:text-sm mt-1 font-sans">{summary?.asset_count || 0} assets across {Object.keys(summary?.by_location || {}).length} locations</p>
          <p className="text-white/45 text-[10px] mt-0.5 font-sans" data-testid="portfolio-scope-note">Purchase value · today's FX · excl. fees</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchive(true)}
            data-testid="open-archive-btn"
            className="border border-white/15 text-white/85 px-3 sm:px-4 py-2.5 text-sm hover:border-white/30 hover:text-white transition-colors flex items-center gap-2 min-h-[44px]"
            title="View archived properties"
          >
            <Archive className="w-4 h-4" /> <span className="hidden sm:inline">Archive</span>
          </button>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-asset-btn"
            className="flex-1 sm:flex-none bg-[#D4AF37] text-black px-4 sm:px-5 py-2.5 text-sm font-medium hover:bg-[#B5952F] transition-colors flex items-center justify-center gap-2 min-h-[44px]"
          >
            <Plus className="w-4 h-4" /> Add Asset
          </button>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {/* Equity Growth Chart — spans 3 */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="col-span-1 md:col-span-2 lg:col-span-3 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6 min-h-[340px]"
          data-testid="performance-chart"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-[11px] tracking-[0.15em] uppercase text-white/60">Total Invested Capital</p>
                <span
                  className="group relative inline-flex items-center justify-center w-4 h-4 border border-white/15 rounded-full text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 cursor-help transition-colors"
                  data-testid="equity-info-icon"
                >
                  <Info className="w-2.5 h-2.5" strokeWidth={2} />
                  <span className="invisible group-hover:visible absolute left-1/2 top-6 -translate-x-1/2 z-20 w-72 max-w-[calc(100vw-2rem)] bg-[#0A0A0A] border border-white/[0.1] text-white/90 text-[11px] leading-relaxed p-3 shadow-2xl">
                    Total committed capital per asset = purchase price + acquisition costs (DLD, fees, taxes), converted to EUR at the FX rate on each asset's purchase date and frozen. Loan amount is not subtracted. The line moves only when an asset is added or removed — never with live FX. Currency drift since acquisition is shown separately in the FX-impact badge.
                  </span>
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
              <div className="text-right" data-testid="total-invested-capital-display">
                <p className="text-[9px] tracking-[0.15em] uppercase text-white/65 mb-0.5 whitespace-nowrap">Invested capital · FX at acquisition</p>
                <p className="font-mono text-base sm:text-lg text-[#D4AF37] leading-none">
                  {(() => {
                    // Cost-basis total for the latest point on the chart —
                    // always in EUR, never re-converted, to stay in lock-step
                    // with the FX-static line itself.
                    const eurVal = Number(performance[performance.length - 1]?.value || 0);
                    return `€ ${Math.round(eurVal).toLocaleString()}`;
                  })()}
                </p>
              </div>
              {/* FX impact badge — committed capital today minus committed capital
                  at each asset's purchase date, converted via the FX rates that
                  were live at the time. Surfaces the wealth effect of currency
                  drift since acquisition. Hidden when 0 (e.g. EUR-only portfolios).
                  Shows "—" when FX history is missing for a purchase date, rather
                  than a misleading 0. */}
              {perfMeta.fx_basis_available === false ? (
                <span
                  data-testid="fx-impact-badge"
                  title="FX impact unavailable: no FX-rate history exists for one or more assets' purchase dates."
                  style={{
                    fontSize: "11px",
                    backgroundColor: "rgba(255,255,255,0.04)",
                    border: "0.5px solid #222",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    color: "#666",
                    whiteSpace: "nowrap",
                  }}
                >
                  FX impact:&nbsp;<span style={{ color: "#888" }}>—</span>
                </span>
              ) : Math.abs(perfMeta.fx_impact_eur || 0) >= 1 && (() => {
                const eur = Number(perfMeta.fx_impact_eur || 0);
                const d = display(Math.abs(eur), "EUR");
                const sign = eur >= 0 ? "+" : "−";
                const color = eur >= 0 ? "#7ab87a" : "#c47a7a";
                return (
                  <span
                    data-testid="fx-impact-badge"
                    title="FX impact = current EUR value of all assets − cost basis in EUR at the time of purchase."
                    style={{
                      fontSize: "11px",
                      backgroundColor: "rgba(255,255,255,0.04)",
                      border: "0.5px solid #222",
                      borderRadius: "4px",
                      padding: "4px 10px",
                      color: "#666",
                      whiteSpace: "nowrap",
                    }}
                  >
                    FX impact:&nbsp;
                    <span style={{ color, fontVariantNumeric: "tabular-nums" }}>
                      {sign}{d.symbol}&nbsp;{Math.round(d.value).toLocaleString()}
                    </span>
                  </span>
                );
              })()}
              <div className="flex border border-white/[0.12]" data-testid="perf-months-toggle">
                {[[2, "2M"], [6, "6M"], [12, "12M"]].map(([val, label]) => (
                  <button key={val} onClick={() => setPerfMonths(val)}
                    className={`px-2.5 sm:px-3 py-1.5 text-[10px] tracking-wider transition-all min-h-[32px] ${
                      perfMonths === val ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-white/65 hover:text-white/65"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Custom legend strip — only renders chips for the locations the
              user actually owns. Small 10×10 squares + uppercase microtype to
              match the Quiet Luxury aesthetic and keep visual weight low. */}
          {performance.length > 0 && (perfMeta.locations_present?.length || 0) > 0 && (
            <div className="flex flex-wrap items-center gap-4 mb-3" data-testid="chart-legend">
              {[
                { key: "dubai",        label: "Dubai, UAE",   color: "rgba(212,175,55,0.65)" },
                { key: "germany",      label: "Germany",      color: "rgba(74,74,74,0.85)" },
                { key: "north_cyprus", label: "North Cyprus", color: "rgba(42,58,42,0.85)" },
              ].filter((it) => (perfMeta.locations_present || []).includes(it.key)).map((it) => (
                <div key={it.key} className="flex items-center gap-2" data-testid={`legend-${it.key}`}>
                  <span style={{
                    display: "inline-block", width: "10px", height: "10px",
                    borderRadius: "2px", backgroundColor: it.color,
                  }} />
                  <span style={{
                    fontSize: "11px", textTransform: "uppercase",
                    letterSpacing: "0.07em", color: "#555",
                  }}>{it.label}</span>
                </div>
              ))}
            </div>
          )}

          {performance.length > 0 ? (() => {
            // ── Stacked-area chart data prep ──────────────────────────────
            // Flatten each datapoint's `by_location` into top-level keys so
            // Recharts can address them as dataKey="dubai" etc. We also
            // convert every per-location EUR value to the user's active
            // currency (EUR/AED/GBP) so the chart obeys the currency toggle.
            // The order matters: lowest-value location first so the largest
            // exposure (typically Dubai) sits on top of the stack and is
            // visible against the dark background.
            const STACK_META = [
              { key: "north_cyprus", label: "North Cyprus", fill: "rgba(42,58,42,0.2)",  stroke: "#2a3a2a", strokeWidth: 1   },
              { key: "germany",      label: "Germany",      fill: "rgba(74,74,74,0.25)", stroke: "#4a4a4a", strokeWidth: 1.5 },
              { key: "dubai",        label: "Dubai, UAE",   fill: "rgba(212,175,55,0.18)", stroke: "#D4AF37", strokeWidth: 1.5 },
            ];
            const activeStack = STACK_META.filter((s) => (perfMeta.locations_present || []).includes(s.key));
            const chartData = performance.map((p) => {
              const out = { month: p.month, _eur_total: p.value };
              for (const s of activeStack) {
                const eurVal = (p.by_location || {})[s.key] || 0;
                // Cost basis is intrinsically in EUR — we deliberately do NOT
                // run it through the EUR/AED/GBP display toggle. Per spec
                // (Jun 3, 2026): the chart line never moves with live FX,
                // and that includes the currency-toggle re-conversion. The
                // FX-impact badge already carries the live-currency story.
                out[s.key] = eurVal;
              }
              out._display_total = p.value;
              return out;
            });
            const ySymbol = "€";
            return (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }}
                    axisLine={false} tickLine={false}
                    interval={perfMonths <= 2 ? 1 : 0}
                  />
                  <YAxis
                    tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 11 }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(v) => {
                      const n = Number(v);
                      return n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : `${Math.round(n)}`;
                    }}
                  />
                  <Tooltip
                    cursor={{ stroke: "rgba(212,175,55,0.25)", strokeWidth: 1 }}
                    content={<StackedAreaTooltip activeStack={activeStack} symbol={ySymbol} />}
                  />
                  {activeStack.map((s) => (
                    <Area
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stackId="invested"
                      stroke={s.stroke}
                      strokeWidth={s.strokeWidth}
                      fill={s.fill}
                      isAnimationActive={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            );
          })() : (
            <div className="flex flex-col items-center justify-center h-[280px]">
              <div className="w-full h-px border-t border-dashed border-white/[0.12] mb-6" />
              <TrendingUp className="w-10 h-10 text-white/[0.06] mb-3" strokeWidth={1} />
              <p className="text-white/60 text-sm">Start adding assets to track your wealth growth</p>
              <p className="text-white/15 text-[10px] mt-1">Equity = Property Value - Remaining Debt, converted to EUR</p>
            </div>
          )}

          {/* Exact explanation of the Total Invested Capital field, placed below the chart */}
          <p
            className="text-white/55 text-[11px] leading-relaxed mt-4 pt-3 border-t border-white/[0.08]"
            data-testid="total-invested-capital-explainer"
          >
            <span className="text-white/70">Total Invested Capital</span> is the total committed capital across all your assets — each asset&apos;s purchase price plus acquisition costs (land-registry / DLD fees, agent fees, taxes), converted to EUR at the FX rate on that asset&apos;s purchase date and then frozen. The loan amount is <span className="text-white/70">not</span> subtracted (this is capital committed, not equity). The line changes only when you add or remove an asset — never with live FX movements. Currency drift since acquisition is shown separately in the FX-impact badge above.
          </p>
        </motion.div>

        {/* FX Rates Panel */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="col-span-1 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
          data-testid="fx-rates-panel"
        >
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">Live FX Rates</p>
          <p className="text-[9px] text-white/55 mb-3">Base: EUR</p>
          <div className="space-y-3">
            {Object.entries(rates).filter(([k]) => k !== "EUR").map(([currency, rate]) => (
              <div key={currency} className="flex items-center justify-between py-2 border-b border-white/[0.08]">
                <span className="font-mono text-xs text-white/85">EUR/{currency}</span>
                <span className="font-mono text-sm text-white">{rate.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Location Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="col-span-1 md:col-span-1 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
          data-testid="location-breakdown"
        >
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">By Location</p>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" stroke="none">
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, color: "#fff", fontSize: 12 }}
                  itemStyle={{ color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                  formatter={(v) => {
                    const d = display(v, "EUR");
                    return `${d.symbol} ${Math.round(d.value).toLocaleString()}`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-white/55 text-sm text-center py-8">No assets yet</p>
          )}
          <div className="mt-3 space-y-1">
            {pieData.map((d, i) => {
              const conv = display(d.value, "EUR");
              return (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-white/65">{d.name}</span>
                  <span className="ml-auto text-white/90 font-mono">
                    {conv.symbol} {Math.round(conv.value).toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Cashflow Ranking Card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="col-span-1 md:col-span-2 lg:col-span-2 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
          data-testid="cashflow-ranking"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[11px] tracking-[0.15em] uppercase text-white/60">Cashflow Ranking</p>
              <p className="text-white/55 text-[10px] mt-0.5">Ranked by net monthly profit ({mode})</p>
            </div>
          </div>

          {(() => {
            const assets = summary?.assets || [];
            if (assets.length === 0) {
              return (
                <div className="text-center py-10">
                  <TrendingUp className="w-8 h-8 text-white/[0.06] mx-auto mb-3" strokeWidth={1} />
                  <p className="text-white/60 text-sm">Add your first asset to see your Cashflow Ranking.</p>
                </div>
              );
            }

            // Build ranked list with EUR conversion
            const ranked = assets
              .map(a => {
                const localCashflow = a.roi?.monthly_cashflow || 0;
                const currency = a.currency || "EUR";
                const eurCashflow = toEur(localCashflow, currency);
                return { ...a, localCashflow, eurCashflow };
              })
              .sort((a, b) => b.eurCashflow - a.eurCashflow);

            const totalEurCashflow = ranked.reduce((s, a) => s + Math.max(a.eurCashflow, 0), 0);
            const maxAbsCashflow = Math.max(...ranked.map(a => Math.abs(a.eurCashflow)), 1);

            return (
              <div className="space-y-0">
                {ranked.map((a, i) => {
                  const d = display(a.localCashflow, a.currency);
                  const isPositive = d.value >= 0;
                  const barWidth = maxAbsCashflow > 0 ? (Math.abs(a.eurCashflow) / maxAbsCashflow) * 100 : 0;
                  const contribution = totalEurCashflow > 0 && a.eurCashflow > 0
                    ? Math.round((a.eurCashflow / totalEurCashflow) * 100) : 0;

                  return (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 sm:gap-3 py-3 border-b border-white/[0.06] last:border-0 group"
                      data-testid={`cashflow-rank-${i}`}
                    >
                      {/* Rank */}
                      <span className={`w-6 sm:w-7 text-center font-mono text-sm flex-shrink-0 ${
                        i === 0 ? "text-[#D4AF37]" : i === 1 ? "text-white/65" : "text-white/60"
                      }`}>
                        #{i + 1}
                      </span>

                      {/* Location icon */}
                      <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${
                        a.location === "dubai" ? "bg-[#D4AF37]/10" : a.location === "germany" ? "bg-[#D4AF37]/[0.06]" : "bg-white/[0.04]"
                      }`}>
                        <span className="text-[10px] font-mono font-medium ${a.location === 'dubai' ? 'text-[#D4AF37]' : 'text-white/60'}">
                          {a.location === "dubai" ? "AE" : a.location === "germany" ? "DE" : "CY"}
                        </span>
                      </div>

                      {/* Name + bar */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white/90 text-sm truncate group-hover:text-white transition-colors">{a.name}</p>
                        <div className="mt-1.5 h-1 bg-white/[0.03] w-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${barWidth}%` }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className={`h-full ${isPositive ? "bg-emerald-500/50" : "bg-red-500/40"}`}
                          />
                        </div>
                      </div>

                      {/* Cashflow amount */}
                      <span className={`font-mono text-xs sm:text-sm font-medium text-right min-w-[80px] sm:min-w-[100px] flex-shrink-0 ${
                        isPositive ? "text-emerald-400" : "text-red-400"
                      }`} data-testid={`cashflow-value-${i}`}>
                        {fmtSigned(a.localCashflow, a.currency)}
                      </span>
                    </div>
                  );
                })}

                {/* Total row */}
                {ranked.length > 1 && (() => {
                  const totalEurMonth = ranked.reduce((s, a) => s + a.eurCashflow, 0);
                  const totalInMode = display(totalEurMonth, "EUR");
                  const totalIsPositive = totalEurMonth >= 0;
                  return (
                    <div className="flex items-center gap-3 pt-3 mt-1 border-t border-[#D4AF37]/10">
                      <span className="w-7" />
                      <span className="w-7" />
                      <p className="flex-1 text-white/60 text-xs tracking-wider uppercase">Total Monthly</p>
                      <span className="w-8" />
                      <span className={`font-mono text-sm font-medium text-right min-w-[100px] ${
                        totalIsPositive ? "text-emerald-400" : "text-red-400"
                      }`} data-testid="cashflow-total">
                        {totalInMode.symbol} {Math.round(totalInMode.value).toLocaleString()}/mo
                      </span>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </motion.div>

        {/* Arbitrage Alerts — placed after Cashflow Ranking so it lands in column 4, directly under Live FX Rates */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.38 }}
          className="col-span-1 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
          data-testid="arbitrage-alerts"
        >
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">Currency Arbitrage Alerts</p>
          {alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map((a, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/[0.08]">
                  {a.direction === "favorable" ? (
                    <TrendingUp className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-white text-sm">{a.message}</p>
                    <p className="text-white/65 text-xs mt-1 font-mono">
                      {a.currency}/EUR: {a.current_rate} (90d avg: {a.avg_90d})
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/55 text-sm py-4">No significant arbitrage opportunities detected. Add assets to monitor FX conditions.</p>
          )}
        </motion.div>

        {/* Assets List */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="col-span-1 md:col-span-3 lg:col-span-4 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
          data-testid="assets-list"
        >
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">Your Assets</p>
          {(summary?.assets || []).length > 0 ? (
            <div className="space-y-3">
              {summary.assets.map((a) => (
                <div
                  key={a.id}
                  className="bg-white/[0.02] border border-white/[0.08] p-4 hover:border-[#D4AF37]/20 transition-all cursor-pointer group"
                  onClick={() => setSelectedAsset(a)}
                  data-testid={`asset-card-${a.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        {a.property_type === "commercial" ? <Landmark className="w-4 h-4 text-[#D4AF37]" /> :
                         a.property_type === "off_plan" ? <Building className="w-4 h-4 text-[#D4AF37]" /> :
                         <Home className="w-4 h-4 text-[#D4AF37]" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate group-hover:text-[#D4AF37] transition-colors">{a.name}</p>
                        <span className="flex items-center gap-1 text-white/55 text-xs"><MapPin className="w-3 h-3" />{locationLabels[a.location] || a.location}</span>
                        {(() => {
                          const ms = (a.payment_milestones || []).filter(m => !m.is_paid);
                          if (ms.length === 0) return null;
                          ms.sort((x, y) => (x.date || "9999").localeCompare(y.date || "9999"));
                          const next = ms[0];
                          const label = next.name || next.description || "Next installment";
                          const amt = next.amount ? fmt(next.amount, a.currency) : "";
                          const when = next.date || next.trigger || "";
                          return (
                            <p className="flex items-center gap-1.5 text-[#D4AF37]/80 text-[10px] mt-1" data-testid={`next-payment-${a.id}`}>
                              <CalendarDays className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">Next: {label}{next.percentage ? ` (${next.percentage}%)` : ""}{amt ? ` · ${amt}` : ""}{when ? ` · ${when}` : ""}</span>
                            </p>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Collapsed KPIs */}
                    <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 sm:ml-4 pl-12 sm:pl-0 -mt-1 sm:mt-0">
                      <div className="text-right">
                        <p className="text-[9px] tracking-[0.1em] uppercase text-white/60">Value</p>
                        <p className="font-mono text-sm text-white">{a.fx_convertible === false ? <span className="text-amber-400" title={`No EUR rate for ${a.currency}`}>N/A</span> : fmt(a.current_value_eur, "EUR")}</p>
                      </div>
                      {a.roi?.monthly_cashflow ? (
                        <>
                          <div className="text-right hidden md:block">
                            <p className="text-[9px] tracking-[0.1em] uppercase text-white/60">Monthly Net</p>
                            <p className={`font-mono text-sm ${a.roi.monthly_cashflow >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {fmt(a.roi.monthly_cashflow, a.currency)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-[9px] tracking-[0.1em] uppercase text-white/60">Total ROI</p>
                            <p className="font-mono text-sm text-[#D4AF37]">{a.roi.total_roi_pct}%</p>
                          </div>
                        </>
                      ) : (
                        <div className="text-right">
                          <p className="text-white/15 text-[10px] italic">No rental data</p>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAsset(a.id); }}
                          data-testid={`delete-asset-${a.id}`}
                          className="text-white/15 hover:text-red-400 transition-colors p-2 -m-1 min-w-[36px] min-h-[36px] flex items-center justify-center"
                          aria-label="Archive asset"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-[#D4AF37]/50 transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/65 text-sm mb-4">No assets in your portfolio yet</p>
              <button
                onClick={() => setShowAdd(true)}
                className="text-[#D4AF37] text-sm hover:text-[#B5952F] transition-colors"
              >
                Add your first property
              </button>
            </div>
          )}
        </motion.div>

        {/* Upcoming Payments — aggregated across all assets */}
        <UpcomingPayments
          assets={summary?.assets || []}
          onRefresh={fetchData}
          fmt={fmt}
        />
      </div>

      <AddAssetDialog open={showAdd} onClose={() => setShowAdd(false)} onSuccess={fetchData} />
      <ArchiveModal open={showArchive} onClose={() => setShowArchive(false)} onAnyChange={fetchData} />
      <OnboardingTour onStartAdding={() => setShowAdd(true)} />
      <AssetDetailModal
        open={!!selectedAsset}
        asset={selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onRefresh={async () => {
          // Re-fetch portfolio data and re-bind the still-open asset to its
          // newest version so edits (rental, milestones, etc.) reflect
          // without closing the modal.
          const fresh = await fetchData();
          setSelectedAsset((curr) => {
            if (!curr) return curr;
            const updated = (fresh?.assets || []).find((a) => a.id === curr.id);
            return updated || curr;
          });
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Upcoming Payments
//
// Aggregates every unpaid milestone across the user's assets and shows them
// chronologically with urgency tiering. Designed to give a single-glance
// answer to "what's hitting my wallet next?".
// ─────────────────────────────────────────────────────────────────────────────

function UpcomingPayments({ assets, onRefresh, fmt }) {
  const [busy, setBusy] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const items = useMemo(() => {
    const rows = [];
    const todayIso = new Date().toISOString().slice(0, 10);
    for (const a of assets) {
      for (const m of a.payment_milestones || []) {
        if (m.is_paid) continue;
        if (!m.date) continue; // skip trigger-only milestones
        const days = Math.round((new Date(m.date) - new Date(todayIso)) / 86400000);
        rows.push({
          assetId: a.id,
          assetName: a.name,
          location: a.location,
          currency: a.currency,
          msId: m.id,
          name: m.name || m.description || "Installment",
          date: m.date,
          amount: m.amount || 0,
          percentage: m.percentage,
          daysUntil: days,
        });
      }
    }
    rows.sort((a, b) => a.date.localeCompare(b.date));
    return rows;
  }, [assets]);

  const totalDueNext30 = useMemo(
    () => items.filter((i) => i.daysUntil <= 30).reduce((sum, i) => sum + (i.amount || 0), 0),
    [items]
  );

  // Note: aggregating amounts across currencies isn't math-meaningful unless we
  // FX-convert. We display the count instead, with a per-row currency badge.
  const next30Count = items.filter((i) => i.daysUntil <= 30).length;
  const visible = showAll ? items : items.slice(0, 6);

  const markPaid = async (row) => {
    if (!row.msId) return;
    setBusy(`${row.assetId}:${row.msId}`);
    try {
      await api.put(`/assets/${row.assetId}/milestones/${row.msId}`, {
        is_paid: true,
        paid_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Marked "${row.name}" as paid`);
      await onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="col-span-1 md:col-span-3 lg:col-span-4 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
        data-testid="upcoming-payments-empty"
      >
        <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 mb-4">Upcoming Payments</p>
        <div className="flex items-center gap-3 py-2 text-white/55 text-sm font-sans">
          <Wallet className="w-4 h-4 text-white/15" strokeWidth={1.5} />
          <span>No scheduled payments — every milestone is paid or none have been added yet.</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="col-span-1 md:col-span-3 lg:col-span-4 bg-[#0A0A0A] border border-white/[0.10] p-4 md:p-6"
      data-testid="upcoming-payments"
    >
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <p className="text-[11px] tracking-[0.15em] uppercase text-white/60">Upcoming Payments</p>
        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65">
          {items.length} open · <span className="text-[#D4AF37]">{next30Count} in 30 days</span>
        </p>
      </div>

      <div className="space-y-1.5">
        {visible.map((row) => (
          <PaymentRow
            key={`${row.assetId}-${row.msId}`}
            row={row}
            fmt={fmt}
            onMarkPaid={() => markPaid(row)}
            busy={busy === `${row.assetId}:${row.msId}`}
          />
        ))}
      </div>

      {items.length > 6 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          data-testid="upcoming-payments-toggle"
          className="mt-4 text-[10px] tracking-[0.2em] uppercase text-white/55 hover:text-[#D4AF37] transition-colors"
        >
          {showAll ? "Show fewer" : `Show all ${items.length}`}
        </button>
      )}

      {totalDueNext30 > 0 && next30Count > 0 && (
        <p className="text-[10px] text-white/60 mt-4 font-sans" data-testid="upcoming-payments-disclaimer">
          Per-currency totals shown per row — cross-currency totals require FX conversion.
        </p>
      )}
    </motion.div>
  );
}

function PaymentRow({ row, fmt, onMarkPaid, busy }) {
  const days = row.daysUntil;
  let urgency;
  if (days < 0) urgency = { label: "Overdue", colour: "text-red-400", border: "border-l-red-500/60", pill: "bg-red-500/10 text-red-300" };
  else if (days <= 7) urgency = { label: `${days}d`, colour: "text-amber-300", border: "border-l-amber-400/60", pill: "bg-amber-500/10 text-amber-300" };
  else if (days <= 30) urgency = { label: `${days}d`, colour: "text-[#D4AF37]", border: "border-l-[#D4AF37]/60", pill: "bg-[#D4AF37]/10 text-[#D4AF37]" };
  else urgency = { label: `${days}d`, colour: "text-white/60", border: "border-l-white/[0.08]", pill: "bg-white/[0.04] text-white/65" };

  return (
    <div
      className={`flex items-center gap-3 bg-white/[0.015] border border-white/[0.08] border-l-2 ${urgency.border} px-3 py-2.5 hover:bg-white/[0.03] transition-colors group`}
      data-testid={`upcoming-payment-${row.assetId}-${row.msId}`}
    >
      {/* Date column */}
      <div className="w-16 flex-shrink-0">
        <p className="font-mono text-xs text-white/90">{row.date.slice(5)}</p>
        <p className={`font-mono text-[10px] ${urgency.colour}`}>{urgency.label}</p>
      </div>

      {/* Description column */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-sans truncate">
          {row.name}
          {row.percentage ? <span className="text-white/60 ml-1.5 text-xs">({row.percentage}%)</span> : null}
        </p>
        <p className="text-white/55 text-[11px] truncate font-sans">{row.assetName}</p>
      </div>

      {/* Amount + currency */}
      <div className="text-right flex-shrink-0">
        {row.amount > 0 ? (
          <p className={`font-mono text-sm ${urgency.colour}`}>
            {fmt(row.amount, row.currency)}
          </p>
        ) : (
          <p className="text-white/55 text-xs italic">No amount</p>
        )}
      </div>

      {/* Mark paid */}
      <button
        type="button"
        onClick={onMarkPaid}
        disabled={busy}
        data-testid={`mark-paid-${row.assetId}-${row.msId}`}
        title="Mark this milestone as paid"
        className="text-white/55 hover:text-emerald-300 hover:bg-emerald-500/[0.06] p-1.5 -m-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed opacity-0 group-hover:opacity-100"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
